'use client'

import { useTranslations } from 'next-intl'
import { ReactNode } from 'react'

import { useSearchFilterStore } from '@/entities/map'
import {
  FilterEnum,
  FilterLayoutProvider,
  FilterProvider,
  Floors,
  Options,
  PropertyTypes,
  PyeongSizes,
  ResetButton,
  RoomCounts,
  RoomDiscounts,
  UsingFee,
  useFilter,
} from '@/features/room-filter'
import { MultiScrollProvider, Slot, useIsTablet } from '@/shared/lib'
import { Button, ScreenOverlay } from '@/shared/ui'
import { MobileHeader } from '@/widgets/common'

type Props = {
  target?: FilterEnum
  children: ReactNode
}

const TABLET_SECTION_CLASS = 'flex flex-row items-start gap-3 px-4'

export const KeywordSearchFilterOverlayTrigger = ({
  target,
  children,
}: Props) => {
  const isTablet = useIsTablet()

  return (
    <ScreenOverlay.Root>
      <ScreenOverlay.Trigger asChild>{children}</ScreenOverlay.Trigger>
      <ScreenOverlay.Content className="flex flex-col">
        <MultiScrollProvider target={target}>
          <FilterProvider>
            <OverlayContent isTablet={isTablet} />
          </FilterProvider>
        </MultiScrollProvider>
      </ScreenOverlay.Content>
    </ScreenOverlay.Root>
  )
}

type OverlayContentProps = {
  isTablet: boolean | null
}

const OverlayContent = ({ isTablet }: OverlayContentProps) => {
  const t = useTranslations('filter')
  const { filter } = useFilter()
  const filterActions = useSearchFilterStore(state => state.actions)

  const handleApply = () => {
    filterActions.updateFilter(filter)
  }

  return (
    <>
      <ScreenOverlay.Title className="sr-only">
        {t('title')}
      </ScreenOverlay.Title>
      <ScreenOverlay.Description className="sr-only">
        {t('description')}
      </ScreenOverlay.Description>
      <MobileHeader.Root>
        <Slot name="left">
          <ScreenOverlay.Close asChild>
            <MobileHeader.Close />
          </ScreenOverlay.Close>
        </Slot>
        <Slot name="title">{t('title')}</Slot>
      </MobileHeader.Root>
      <div className="grow overflow-y-auto pt-6">
        {isTablet ? (
          <FilterLayoutProvider layout="horizontal">
            <div className="flex items-center justify-end px-4">
              <ResetButton />
            </div>
            <div className="space-y-10 pb-6">
              <RoomCounts className={TABLET_SECTION_CLASS} />
              <UsingFee className={TABLET_SECTION_CLASS} />
              <PropertyTypes className={TABLET_SECTION_CLASS} />
              <PyeongSizes className={TABLET_SECTION_CLASS} />
              <Floors className={TABLET_SECTION_CLASS} />
              <Options className={TABLET_SECTION_CLASS} />
              <RoomDiscounts className={TABLET_SECTION_CLASS} />
            </div>
          </FilterLayoutProvider>
        ) : (
          <FilterLayoutProvider layout="mobile">
            <div className="space-y-10 *:px-6">
              <div className="mb-0 flex items-center justify-end">
                <ResetButton />
              </div>
              <RoomCounts />
              <UsingFee />
              <PropertyTypes />
              <PyeongSizes />
              <Floors />
              <Options />
              <RoomDiscounts />
            </div>
          </FilterLayoutProvider>
        )}
      </div>
      <div className="flex flex-row gap-2.5 p-6">
        <ScreenOverlay.Close asChild>
          <Button variant="solid" color="secondary" size="large" fullWidth>
            {t('button.close')}
          </Button>
        </ScreenOverlay.Close>
        <ScreenOverlay.Close asChild>
          <Button size="large" fullWidth onClick={handleApply}>
            {t('button.apply')}
          </Button>
        </ScreenOverlay.Close>
      </div>
    </>
  )
}
